/**
 * Mentions Service
 * API client for mention management operations
 */

import apiClient from './api.client';
import type { Mention, MentionFilters, UnreadMentionCounts } from '@/types/api.types';

export class MentionsService {
  /**
   * Get user's mentions with optional filters
   */
  async getMentions(filters?: MentionFilters): Promise<{ mentions: Mention[]; total: number }> {
    const params = new URLSearchParams();

    if (filters?.channelId) params.append('channelId', filters.channelId);
    if (filters?.dmThreadId) params.append('dmThreadId', filters.dmThreadId);
    if (filters?.isRead !== undefined) params.append('isRead', String(filters.isRead));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const response = await apiClient.get(`/mentions?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get unread mention counts per channel/DM
   */
  async getUnreadCounts(): Promise<UnreadMentionCounts> {
    const response = await apiClient.get('/mentions/unread');
    return response.data.data;
  }

  /**
   * Mark a specific mention as read
   */
  async markAsRead(mentionId: string): Promise<void> {
    await apiClient.patch(`/mentions/${mentionId}/read`);
  }

  /**
   * Mark all mentions in a conversation as read
   */
  async markConversationAsRead(channelId?: string, dmThreadId?: string): Promise<{ count: number }> {
    const response = await apiClient.post('/mentions/read-all', {
      channelId,
      dmThreadId,
    });
    return response.data;
  }
}

export const mentionsService = new MentionsService();
